import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for token in query params (direct Google OAuth)
      const token = searchParams.get('token');
      const redirect = searchParams.get('redirect') || '/dashboard';
      const error = searchParams.get('error');
      
      if (error) {
        console.error('Auth error:', error);
        navigate('/login?error=' + error);
        return;
      }

      if (token) {
        // Direct Google OAuth - token in URL
        try {
          console.log('AuthCallback: Token received, storing...');
          // Store token in localStorage first so axios interceptor can use it
          localStorage.setItem('subguard_token', token);
          
          console.log('AuthCallback: Fetching user data...');
          const userResponse = await authApi.getMe();
          const fullUser = userResponse.data;
          console.log('AuthCallback: User data received', fullUser);
          
          // Now call login to update context state
          login(token, fullUser);
          
          console.log('AuthCallback: Navigating to', redirect);
          navigate(redirect, { replace: true });
        } catch (error) {
          console.error('Auth callback error:', error);
          console.error('Error details:', error.response?.data || error.message);
          navigate('/login?error=callback_failed');
        }
        return;
      }

      // Legacy: Check for session_id in hash (Emergent OAuth)
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await authApi.googleSession(sessionId);
          const userData = response.data.user;
          
          login('google_oauth', userData);
          
          const userResponse = await authApi.getMe();
          const fullUser = userResponse.data;
          login('google_oauth', fullUser);
          
          if (!fullUser.onboarding_completed) {
            navigate('/onboarding', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login');
        }
        return;
      }

      // No auth data found
      navigate('/login');
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B] mx-auto mb-4"></div>
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
